import type { AppSettings } from '../stores/types/settings';
import type { Shot } from '../stores/types/shots';
import { FEED_TYPES } from '../stores/reducers/shots';
import { HttpRequest } from '../stores/types/types';
import { Entity } from '../types/interfaces';
import { getAllShotOverlays } from './graphics';
import { buildSyncUrl, getAuthenticatedRequestInit, getSyncHeaders } from './utils';
import { getEmbedSafeHeaders } from './auth';
import blpAuth from './blp-auth';

export const LoadShots = (
	token: string,
	meetingId: number,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/load`, {
	method: 'POST',
	body: JSON.stringify({ meetingId }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		return data;
	}
	throw new Error(`Could not load shots for meeting id ${meetingId}`);
});

export const LoadShot = (token: string, meetingId: number, shotId: number) => fetch(`${process.env.REACT_APP_SYNC_HOST}/v1/room/${meetingId}/load-shot/${shotId}`, {
	method: 'GET',
	headers: getSyncHeaders(token),
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		return data;
	}
	throw new Error(`Could not load shots for meeting id ${meetingId}`);
});

export const AddShot = (token: string, shot: Shot, session: string): Promise<Shot> => {
	const meetingId = shot.meeting;
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/add`, {
		method: 'POST',
		body: JSON.stringify({ shot, meetingId, session }),
		...getAuthenticatedRequestInit(token),
	}).then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error('Unable to create shot');
	}).catch((error) => {
		console.error(error);
	});
};

export const GenerateShotThumbnail = (
	token: string,
	meetingSlug: string,
	shotId: number,
	channelId: number,
	room?: number,
): Promise<void> => {
	const url = buildSyncUrl('v1/shots/generate');
	return fetch(url, {
		method: HttpRequest.Post,
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			meetingId: meetingSlug,
			shotId,
			channelId,
			room,
		}),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed to generate shot thumbnail');
		}
	}).catch((error) => {
		console.error(error);
		throw error;
	});
};

export const GenerateAllShotThumbnails = (
	token: string,
	meetingSlug: string,
	channelId: number,
	room: number,
	onlyMissing?: boolean,
): Promise<void> => {
	const url = buildSyncUrl('v1/shots/generate-all');
	return fetch(url, {
		method: HttpRequest.Post,
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			meetingId: meetingSlug,
			channelId: channelId,
			room: room,
			...(onlyMissing && { onlyMissing }),
		}),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed to generate all shot thumbnails');
		}
	}).catch((error) => {
		console.error(error);
		throw error;
	});
};

export const EditShot = (
	token: string,
	shot: Shot,
	meetingSlug?: string,
	channelId?: number,
	useNewShotThumbs: boolean = false,
) => {
	const meetingId = shot.meeting;
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/edit`, {
		method: 'POST',
		body: JSON.stringify({ shot, meetingId }),
		...getAuthenticatedRequestInit(token),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed to edit shot');
		} else {
			// Get updated overlays when the shot is edited
			// There may be a better way to do this
			// Does this do nothing?
			getAllShotOverlays(token, meetingId, true).catch((err) => {
				console.error(err);
			});

			const hasContent = shot.shot_data.shotFeeds?.some(
				(feed) => !feed.audioOnly && feed.feedType !== FEED_TYPES.EMPTY
			);

			if (!hasContent || !meetingSlug || !channelId || !useNewShotThumbs) return;

			// Generate shot thumbnail if meeting slug and channel are provided and if the shot is not empty
			GenerateShotThumbnail(
				token,
				meetingSlug,
				shot.shot_id,
				channelId,
				meetingId, // room
			).catch((err) => {
				console.error(err);
			});

		}
	}).catch((error) => {
		console.error(error);
	});
};

export const BulkEditShots = (token: string, shots: Shot[], deprecated: string = ''): Promise<void> => {
	const meetingId = shots[0]?.meeting ?? 0;
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/bulk-edit`, {
		method: 'POST',
		body: JSON.stringify({ shots, meetingId, deprecated }),
		...getAuthenticatedRequestInit(token),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed bulk editing shots');
		}
	}).catch((error) => {
		console.error(error);
	});
};

export const DeleteShot = (token: string, shotId: number, meetingId: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/delete`, {
	method: 'POST',
	body: JSON.stringify({ shotId, meetingId }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (!result.ok) {
		throw new Error('Failed deleting shot');
	}
}).catch((error) => {
	console.error(error);
});

export const DuplicateShot = (token: string, shotId: number, order: number, session: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/duplicate`, {
	method: 'POST',
	body: JSON.stringify({ shotId, order, session }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error('Failed to duplicate shot');
}).catch((error) => {
	console.error(error);
});

export const PutCanvasStyle = (token: string, meetingId: number, canvasStyle: AppSettings) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/style`, {
	method: 'PUT',
	body: JSON.stringify({ meetingId, canvasStyle }),
	...getAuthenticatedRequestInit(token),
}).catch((error) => {
	console.error(error);
});

export const updateCurrentShotIdEntity = async (
	token: string,
	roomId: number,
	newCurrentShotIdEntity: Entity,
	socketId: string,
	isPlaying: boolean,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/current-shot-id`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
			body: JSON.stringify({ newCurrentShotIdEntity, socketId, isPlaying }),
		});
		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}
	return false;
};

export const updateShotStartTimestampEntity = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/shot-start-timestamp`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
		});
		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}
	return false;
};

export const clearShotStartTimestampEntity = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/clear-shot-start-timestamp`);
		const res = await fetch(url, {
			method: HttpRequest.Post,
			headers: getSyncHeaders(token),
		});
		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}
	return false;
};

export const getShotStartTimestampEntity = async (
	token: string,
	roomId: number,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/shot-start-time-update`);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});
		const entity = await res.json();
		return entity;
	} catch (error) {
		console.error(error);
		return null;
	}
};

export const BulkDeleteShots = async (
	roomId: number,
	shotIds: number[],
): Promise<boolean> => {
	try {
		await blpAuth.post({
			host: process.env.REACT_APP_SYNC_HOST,
			url: `/v1/room/${roomId}/shots/bulk-delete`,
			token: blpAuth.accessToken,
			options: { headers: getEmbedSafeHeaders() },
			body: { shotIds },
		});
		return true;
	} catch (error) {
		console.error('Error bulk deleting shots:', error);
		return false;
	}
};

export const getCurrentShotIdEntity = async (
	token: string,
	roomId: number,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/current-shot-id`);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});
		if (res.status === 200) {
			const entity = await res.json();
			return entity;
		} else {
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
};
